import { useTranslation } from 'react-i18next';
import ModalPage from '../../Common/Modal/ModalPage';
import PaymentLinkActions from './PaymentLinkActions';
import { convertDoubleToStringPTBR } from '../../../../utils/ConverterUtils';
import { formatDate, formatDateTime } from '../../../../utils/DateUtils';
import './SubscriptionPaymentLinkModal.css';

/**
 * Modal aberto logo depois de cadastrar um assinante pelo fluxo "enviar link para o
 * cliente". É o único momento em que o operador tem o cliente na frente dele, então a
 * tela mostra o que ele precisa conferir antes de mandar (valor, quem, vencimento) e
 * deixa o envio a um toque.
 *
 * @param {object} charge cobrança recém-criada; sem ela o modal não abre
 * @param {function} setCharge atualiza a cobrança quando o link é gerado na hora
 * @param {string} phone telefone do cliente, para o botão do WhatsApp
 * @param {string} planName nome do plano assinado, só para exibição
 * @param {function} onClose chamado ao fechar, para a tela seguir seu fluxo
 */
const SubscriptionPaymentLinkModal = ({ charge, setCharge, phone, planName, onClose }) => {

    const { t } = useTranslation();

    const paymentLink = charge?.paymentLink;

    // Mesmo formato de dinheiro usado na tela de cobranças.
    const formatMoney = (amount) => `${t('currency_symbol')} ${amount != null ? convertDoubleToStringPTBR(amount) : '0,00'}`;

    // Cliente e plano numa linha só, pulando o que estiver faltando para não sobrar
    // um separador solto.
    const subtitle = [charge?.customerName, planName].filter(Boolean).join(' · ');

    return (
        <ModalPage
            show={charge != null}
            setShow={onClose}
            btnCloseOnClick={onClose}
            centered
            dialogClassName="subscription-link-modal"
            title={
                <>
                    <i className="fas fa-link me-2" aria-hidden="true"></i>
                    {t('subscription_payment_link_ready_title')}
                </>
            }
            child={charge && (
                <div className="subscription-link">
                    <section className="subscription-link__summary">
                        <span className="subscription-link__badge">
                            <i className="fa-regular fa-clock me-1" aria-hidden="true"></i>
                            {t('subscription_payment_link_waiting')}
                        </span>

                        <h3 className="subscription-link__amount">{formatMoney(charge.amount)}</h3>

                        {subtitle && <p className="subscription-link__subtitle">{subtitle}</p>}

                        {charge.dueDate && (
                            <p className="subscription-link__due">
                                <i className="fa-regular fa-calendar me-1" aria-hidden="true"></i>
                                {t('subscription_payment_link_due', { date: formatDate(charge.dueDate) })}
                            </p>
                        )}

                        {/* O prazo do link é diferente do vencimento da mensalidade e é a
                            informação que o operador precisa passar ao cliente na hora do
                            envio: depois dele, o link não aceita mais pagamento. */}
                        {charge.paymentLinkExpiresAt && !charge.paymentLinkExpired && (
                            <p className="subscription-link__due">
                                <i className="fa-regular fa-hourglass-half me-1" aria-hidden="true"></i>
                                {t('payment_link_valid_until', { date: formatDateTime(charge.paymentLinkExpiresAt) })}
                            </p>
                        )}
                    </section>

                    <p className="subscription-link__hint">
                        {paymentLink
                            ? t('subscription_payment_link_ready_hint')
                            : t('subscription_payment_link_pending_hint')}
                    </p>

                    {/* O link cru fica à vista para o operador conferir antes de mandar.
                        Copiar e abrir seguem nos botões abaixo, sem repetir a ação aqui. */}
                    {paymentLink && (
                        <div className="subscription-link__url">
                            <i className="fas fa-link subscription-link__url-icon" aria-hidden="true"></i>
                            <span className="subscription-link__url-value" title={paymentLink}>{paymentLink}</span>
                        </div>
                    )}

                    <div className="subscription-link__actions">
                        <PaymentLinkActions
                            charge={charge}
                            phone={phone}
                            onLinkGenerated={(link) => setCharge((prev) => ({ ...prev, paymentLink: link }))}
                            onPixEnabled={(link) => setCharge((prev) => ({
                                ...prev,
                                paymentLink: link,
                                paymentMethod: 'PIX'
                            }))}
                        />
                    </div>
                </div>
            )}
        />
    );
};

export default SubscriptionPaymentLinkModal;
